import { Icon } from "@captions-web/icons";
import { isAbortOrCanceledError } from "@captions-web/utils/abort-signals";
import { useQueryErrorResetBoundary } from "@tanstack/react-query";
import { ErrorComponentProps, useRouter } from "@tanstack/react-router";
import { t } from "i18next";
import { useEffect } from "react";

import { Button, LinkButton } from "~/components/primitives/button";
import { addDataDogError } from "~/lib/analytics/datadog";
import { ErrorEvents } from "~/lib/analytics/defs/ErrorEvents";
import { useOnMountEffect } from "~/lib/hooks/react-helpers";
import { css } from "~/styled-system/css";

import { MirageLogoLarge } from "./mirage-logo";
import { NoiseGrid } from "./noise-grid";
import { Dialog } from "./primitives/dialog";

export function NotFoundComponent() {
  return (
    <div>
      <NoiseGrid />
      <div
        className={css({
          position: "relative",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: "8px",
          height: "100dvh",
          pointerEvents: "none",
        })}
      >
        <MirageLogoLarge />
        <h1 className={css({ textStyle: "heading-1" })}>Mirage Studio</h1>
        <p className={css({ textStyle: "body-1-subtle" })}>
          The page you are looking for does not exist.
        </p>
        <LinkButton
          to="/home"
          variant="secondary"
          size="md"
          iconPosition="after"
          icon={<Icon name="right-arrow-small" />}
          className={css({
            marginTop: "24px",
            pointerEvents: "auto",
          })}
        >
          Go home
        </LinkButton>
      </div>
    </div>
  );
}

export function DefaultErrorComponent(props: ErrorComponentProps) {
  const queryErrorResetBoundary = useQueryErrorResetBoundary();
  const isAnInstanceOfAbortOrCanceledError = isAbortOrCanceledError(props.error);
  ErrorEvents.uncaught_error.useTrackEventOnMount({
    boundary: "default",
    error_message: props.error.message,
    error_name: props.error.name,
    error_stack: props.error.stack,
    error_component_stack: props.info?.componentStack,
  });

  useOnMountEffect(() => {
    addDataDogError(props.error.message, {
      boundary: "default",
      error_message: props.error.message,
      error_name: props.error.name,
      error_stack: props.error.stack,
      error_component_stack: props.info?.componentStack,
    });
  });

  useEffect(() => {
    if (isAnInstanceOfAbortOrCanceledError) {
      queryErrorResetBoundary.reset();
    }
  }, [isAnInstanceOfAbortOrCanceledError, queryErrorResetBoundary]);

  if (isAnInstanceOfAbortOrCanceledError) {
    return null;
  }

  return (
    <ErrorDialog
      description={t("mirage-studio:error.description")}
      errorMessage={props.error.message}
      cta={
        <Button variant="secondary" onClick={() => window.location.reload()}>
          {t("common:reload")}
        </Button>
      }
    />
  );
}

// copied from: https://tanstack.com/router/latest/docs/framework/react/guide/external-data-loading#error-handling-with-tanstack-query
export function SuspenseErrorComponent(props: ErrorComponentProps) {
  const router = useRouter();
  const queryErrorResetBoundary = useQueryErrorResetBoundary();
  const isAnInstanceOfAbortOrCanceledError = isAbortOrCanceledError(props.error);

  useEffect(() => {
    queryErrorResetBoundary.reset();
  }, [queryErrorResetBoundary]);

  useEffect(() => {
    if (isAbortOrCanceledError(props.error)) {
      queryErrorResetBoundary.reset();
    }
  }, [props.error, queryErrorResetBoundary]);

  ErrorEvents.uncaught_error.useTrackEventOnMount({
    boundary: "suspense",
    error_message: props.error.message,
    error_name: props.error.name,
    error_stack: props.error.stack,
    error_component_stack: props.info?.componentStack,
  });

  useOnMountEffect(() => {
    addDataDogError(props.error.message, {
      boundary: "suspense",
      error_message: props.error.message,
      error_name: props.error.name,
      error_stack: props.error.stack,
      error_component_stack: props.info?.componentStack,
    });
  });

  if (isAnInstanceOfAbortOrCanceledError) {
    return null;
  }

  return (
    <ErrorDialog
      description={t("mirage-studio:error.description")}
      errorMessage={props.error.message}
      cta={
        <Button
          variant="secondary"
          onClick={() => {
            router.invalidate();
          }}
        >
          {t("common:retry")}
        </Button>
      }
    />
  );
}

function ErrorDialog({
  description,
  errorMessage,
  cta,
}: {
  description: string;
  errorMessage: string;
  cta: React.ReactNode;
}) {
  return (
    <Dialog.Root open>
      <Dialog.SimpleContent
        title={<span className={css({ color: "$error" })}>{t("common:error")}</span>}
        className={css({
          maxWidth: "400px",
          width: "100%",
        })}
        cta={cta}
        hideCloseButton
        closeOnOutsideClick={false}
        closeOnEscapeKey={false}
      >
        <Dialog.Description className={css({ textStyle: "body-1-subtle" })}>
          {description}
        </Dialog.Description>
        <pre
          className={css({
            padding: "4px",
            borderRadius: "4px",
            background: "$surface.highlight",
            textStyle: "body-3-subtle",
            whiteSpace: "pre-wrap",
          })}
        >
          {errorMessage}
        </pre>
      </Dialog.SimpleContent>
    </Dialog.Root>
  );
}
